import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, firstValueFrom } from 'rxjs';
import { disconnect } from '@wagmi/core';
import { LocalStorageService } from './local-storage.service';
import { WagmiConfigService, BSC_SWITCH_REQUIRED_MESSAGE } from './wagmi-config.service';
import { Web3AuthService } from './web3-auth.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { getWalletAddress } from 'src/app/shared/utils/wallet-state';
import { requireWagmiConfig } from 'src/app/shared/utils/wagmi-client';

@Injectable({
  providedIn: 'root'
})
export class WalletAuthOrchestratorService implements OnDestroy {
  private subscription?: Subscription;
  private routeSubscription?: Subscription;
  private isSigningIn = false;
  private lastSignedAddress: string | null = null;
  private lastSeenAddress: string | null = null;
  private lastCheckedAddress: string | null = null;
  private isCheckingUser = false;
  /** Address check-user last reported "not found" for — de-dupes the toast + signup redirect
   *  across repeat triggers (tab focus, resume-from-background, ...) for the same wallet. */
  private lastNotFoundAddress: string | null = null;

  constructor(
    private readonly wagmiConfigService: WagmiConfigService,
    private readonly web3AuthService: Web3AuthService,
    private readonly authService: AuthService,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly toastrService: ToastrService
  ) {}

  start() {
    if (this.subscription) return;
    // Hydrate last known wallet from storage (if any)
    const bootStoredWallet =
      (this.localStorageService.getDataByKey('walletAddress') ?? '')?.toLowerCase?.() ?? '';
    if (bootStoredWallet) this.lastSeenAddress = bootStoredWallet;

    // If user lands on wallet-connect with an already-connected wallet,
    // wagmi's account$ may not emit again. Trigger check-user on route entry.
    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.router.url.includes('wallet-connect')) return;
        // Only proceed if wagmi reports an active account; otherwise we may trigger
        // signature flow while disconnected, leading to "Connector not found".
        let address = '';
        try {
          address = (getWalletAddress() ?? '').toLowerCase();
        } catch {}
        if (!address) return;
        this.lastSeenAddress = address;
        this.ensureUserChecked(address, false).catch(() => {});
      });

    this.subscription = this.wagmiConfigService.account$.subscribe((account) => {
      // `account$` is a BehaviorSubject seeded with `undefined` ("not yet resolved") that later
      // emits either `null` (WagmiConfigService.emitDisconnected() — a *confirmed* disconnect)
      // or a connected `{ account, chainId }` object. Those first two both read as a falsy
      // `address` below, but only `null` means the wallet is actually gone — `undefined` just
      // means boot hasn't settled yet, and re-emits synchronously the moment `start()`
      // subscribes, i.e. immediately after the localStorage seed below. Resetting on both would
      // wipe that seed before a real emission ever arrives, permanently disabling wallet-changed
      // detection across a reload. Resetting on neither (the previous behavior) left this stuck
      // at a stale address after any real disconnect (e.g. the "Contribute" CTA's own
      // disconnect() call), so the very next connect — even with a perfectly ordinary, different
      // wallet — read as "changed" and forced a spurious logout + redirect home.
      if (account === null) {
        this.lastSeenAddress = null;
        // A confirmed disconnect ends this wallet's session — the "already told them" marker
        // must not survive it, or reconnecting the same still-unregistered wallet later would
        // silently suppress both the toast and the /signup redirect on ensureUserChecked()'s
        // next 400/404 for it.
        this.lastNotFoundAddress = null;
        return;
      }

      const address = (account?.account ?? '').toLowerCase();
      if (!address) return;

      const previousAddress = this.lastSeenAddress;
      // A confirmed disconnect resets lastSeenAddress to null (see the branch above), which would
      // otherwise make the very next connect — even with a genuinely different wallet — read as
      // unchanged. Fall back to the wallet a stored session's token actually belongs to, which
      // survives that reset (and a page reload, unlike lastSeenAddress before this subscription
      // re-hydrates it): a token issued for the old wallet must never coexist with a different
      // wallet's connection, even outside the wallet-connect page.
      const storedWallet = (this.localStorageService.getDataByKey('walletAddress') ?? '')?.toLowerCase?.() ?? '';
      const hasStaleSession = !!storedWallet && storedWallet !== address && !!this.localStorageService.getToken();
      const isWalletChanged = (!!previousAddress && previousAddress !== address) || hasStaleSession;
      this.lastSeenAddress = address;

      // Requirement update: On wallet change, force a full disconnect and reset,
      // then send user to home. User can reconnect again from wallet-connect page.
      if (isWalletChanged) {
        // Single source of truth for the full teardown (wagmi disconnect + every
        // session/wallet-identity localStorage key) — see WagmiConfigService.disconnectAndClearSession().
        void this.wagmiConfigService.disconnectAndClearSession();
        this.localStorageService.clearReferralIds();
        this.lastSignedAddress = null;
        this.lastCheckedAddress = null;
        this.lastNotFoundAddress = null;
        this.router.navigate(['']);
        return;
      }

      // Always call check-user on connect/change events.
      // - If check-user says "not found" -> go signup.
      // - If wallet changed and check-user succeeds -> go home ('/').
      // - If wallet-connect page and user exists -> proceed with signature login flow (or skip if session already exists).
      this.ensureUserChecked(address, isWalletChanged).catch(() => {});
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }

  private async signatureLoginFlow(address: string) {
    if (this.isSigningIn) return;
    if (this.lastSignedAddress === address && this.localStorageService.getToken()) return;

    this.isSigningIn = true;
    try {
      // User existence is checked in ensureUserChecked() before reaching signature flow.

      // Block login while the wallet is off BSC (or while Wagmi's connector/connection chain
      // ids still disagree after a mobile switch). Signing in that state surfaces
      // ConnectorChainMismatchError with a useless "id: 1 does not match id: 1" toast.
      const onExpectedChain = await this.wagmiConfigService.ensureCorrectChain();
      if (!onExpectedChain) {
        // ensureCorrectChain / switchNetwork already toasted the user-facing message.
        return;
      }

      const { nonce_token, message } = await this.web3AuthService.requestNonce(address);

      let signature: string;
      try {
        signature = await this.web3AuthService.signLoginMessage(address, message);
      } catch (err: any) {
        const msg = err?.shortMessage || err?.message || '';
        const lower = String(msg).toLowerCase();
        // Web3Modal/Wagmi can briefly report "Connector not found" right after connect.
        // Retry once after a short delay.
        if (lower.includes('connector not found')) {
          await new Promise((r) => setTimeout(r, 300));
          signature = await this.web3AuthService.signLoginMessage(address, message);
        } else if (WagmiConfigService.isChainMismatchError(err)) {
          // Same pattern for a half-finished mobile chain switch: wait, re-ensure BSC, retry once.
          await new Promise((r) => setTimeout(r, 500));
          const retried = await this.wagmiConfigService.ensureCorrectChain({ showToasts: false });
          if (!retried) {
            throw Object.assign(new Error(BSC_SWITCH_REQUIRED_MESSAGE), { name: 'ConnectorChainMismatchError' });
          }
          signature = await this.web3AuthService.signLoginMessage(address, message);
        } else {
          throw err;
        }
      }
      const response: any = await this.web3AuthService.loginWithSignature(address, nonce_token, signature, message);

      this.localStorageService.storeData('userDetails', response.data);
      this.localStorageService.storeData('userProfile', { user: response.data.user });
      this.localStorageService.storeData('walletAddress', response.data.user.wallet_address);
      this.localStorageService.setToken(response.data.session.session_token);
      this.localStorageService.storeData('is_bounty_user', response.data.is_bounty_user);

      this.lastSignedAddress = address;

      // Preserve existing behavior: only redirect after successful login when user is on wallet-connect page.
      if (this.router.url.includes('wallet-connect')) {
        const redirect = localStorage.getItem('currentUrl');
        if (redirect) {
          localStorage.removeItem('currentUrl');
          this.router.navigate([redirect]);
        } else {
          this.router.navigate(['contributenow']);
        }
      }
    } catch (error: any) {
      const message = WagmiConfigService.isChainMismatchError(error)
        ? BSC_SWITCH_REQUIRED_MESSAGE
        : error?.shortMessage ||
          error?.message ||
          error?.data?.error?.message ||
          'Signature login failed';
      this.toastrService.error(message);

      if (String(message).toLowerCase().includes('rejected')) {
        try { await disconnect(requireWagmiConfig()); } catch {}
      }
    } finally {
      this.isSigningIn = false;
    }
  }

  private async ensureUserChecked(address: string, isWalletChanged: boolean) {
    if (this.isCheckingUser) return;
    // If we already checked this wallet and still have a valid session, skip re-check.
    // But if token was cleared (ex: forced fresh flow), allow check+login again.
    if (this.lastCheckedAddress === address && !isWalletChanged && this.localStorageService.getToken()) return;

    this.isCheckingUser = true;
    try {
      await firstValueFrom(this.authService.getUser(address));
      this.lastCheckedAddress = address;
      // A real account for this address now exists (or already did) — any earlier "not found"
      // for it is no longer the current truth.
      this.lastNotFoundAddress = null;

      if (isWalletChanged) {
        // Wallet change is handled at subscription-level with full disconnect.
        return;
      }

      // Avoid automatic signature prompts on manual refresh or background auto-connect.
      // Only trigger login flow when user is on wallet-connect page.
      if (!this.router.url.includes('wallet-connect')) return;

      // If user already has a valid session for the same wallet, do not prompt signature again.
      const token = this.localStorageService.getToken();
      const storedWallet =
        (this.localStorageService.getDataByKey('walletAddress') ?? '')?.toLowerCase?.() ?? '';
      if (token && storedWallet && storedWallet === address) {
        const redirect = localStorage.getItem('currentUrl');
        if (redirect) {
          localStorage.removeItem('currentUrl');
          this.router.navigate([redirect]);
        } else {
          this.router.navigate(['contributenow']);
        }
        return;
      }

      this.signatureLoginFlow(address);
    } catch (e: any) {
      const status = e?.status ?? e?.error?.status;

      // Status 0 means the response never arrived: CORS rejection, DNS failure, or the device
      // dropping the request. Angular cannot see a CORS-blocked response at all, so it surfaces
      // as an empty error indistinguishable from being offline.
      //
      // This is worth its own branch because the wallet is connected by this point — the user
      // sees a connected wallet and a page that never advances, with a generic toast that reads
      // like a server problem. It is the failure mode when the app is served from an origin the
      // backend does not allow, which is exactly what a dev tunnel or an unregistered staging
      // host is. Naming it stops that from looking like a wallet bug.
      if (status === 0) {
        this.toastrService.error(
          'Cannot reach the server. Check that this origin is allowed by the API (CORS).'
        );
        return;
      }

      if (status === 400 || status === 404) {
        // Already told the user this exact wallet isn't registered — a repeat trigger for the
        // same address (tab regaining focus, resuming from background, ...) would otherwise
        // re-show this toast every time while they're sitting on /signup filling out the form.
        if (this.lastNotFoundAddress === address) return;
        this.lastNotFoundAddress = address;

        const apiMessage =
          e?.error?.message ||
          e?.error?.error?.message ||
          e?.message ||
          'Unable to verify user. Please try again.';
        this.toastrService.error(apiMessage);
        this.router.navigate(['/signup']);
        return;
      }

      const apiMessage =
        e?.error?.message ||
        e?.error?.error?.message ||
        e?.message ||
        'Unable to verify user. Please try again.';
      this.toastrService.error(apiMessage);
    } finally {
      this.isCheckingUser = false;
    }
  }
}
